import { api, apiRequest } from '@/lib/api/client'
import type {
  CampaignDashboardPayload,
  CampaignDonorsResponse,
  CampaignNumberRankingMode,
  CampaignNumberRankingPayload,
  CampaignPaymentListStatus,
  CampaignSaleDetailPayload,
  CampaignSalesResponse,
  CampaignDrawWinnerSavePayload,
  CampaignDrawWinnerStoredPayload,
  CampaignWinnerLookupPayload,
} from '@/types/campaign-report'

export interface CampaignDashboardFilters {
  dateFrom?: string | null
  dateTo?: string | null
}

export interface CampaignPaginationFilters {
  page?: number
  perPage?: number
  dateFrom?: string | null
  dateTo?: string | null
}

export interface CampaignSalesFilters extends CampaignPaginationFilters {
  statuses?: CampaignPaymentListStatus[]
  paymentProvider?: string | null
  dateFrom?: string | null
  dateTo?: string | null
  dateBasis?: 'paid' | 'created'
  q?: string | null
}

function buildQuery(
  params: Record<string, string | number | null | undefined>
): string {
  const search = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    search.set(key, String(value))
  })

  const query = search.toString()
  return query ? `?${query}` : ''
}

export async function fetchCampaignDashboard(
  campaignId: number,
  filters: CampaignDashboardFilters = {},
  token?: string | null
): Promise<CampaignDashboardPayload> {
  const query = buildQuery({
    date_from: filters.dateFrom ?? undefined,
    date_to: filters.dateTo ?? undefined,
  })

  return apiRequest<CampaignDashboardPayload>(
    `/api/campaigns/${campaignId}/dashboard${query}`,
    {
      method: 'GET',
      requireAuth: true,
      token: token ?? undefined,
    }
  )
}

export async function fetchCampaignDonors(
  campaignId: number,
  filters: CampaignPaginationFilters = {},
  token?: string | null
): Promise<CampaignDonorsResponse> {
  const query = buildQuery({
    page: filters.page ?? 1,
    per_page: filters.perPage ?? 10,
    date_from: filters.dateFrom ?? undefined,
    date_to: filters.dateTo ?? undefined,
  })

  return apiRequest<CampaignDonorsResponse>(
    `/api/campaigns/${campaignId}/donors${query}`,
    {
      method: 'GET',
      token: token ?? undefined,
    }
  )
}

export async function fetchCampaignSales(
  campaignId: number,
  filters: CampaignSalesFilters = {},
  token?: string | null
): Promise<CampaignSalesResponse> {
  const statuses = filters.statuses?.filter(Boolean)
  const query = buildQuery({
    page: filters.page ?? 1,
    per_page: filters.perPage ?? 10,
    status:
      statuses && statuses.length > 0 ? statuses.join(',') : undefined,
    payment_provider: filters.paymentProvider ?? undefined,
    date_from: filters.dateFrom ?? undefined,
    date_to: filters.dateTo ?? undefined,
    date_basis: filters.dateBasis ?? undefined,
    q: filters.q?.trim() ? filters.q.trim() : undefined,
  })

  return apiRequest<CampaignSalesResponse>(
    `/api/campaigns/${campaignId}/sales${query}`,
    {
      method: 'GET',
      requireAuth: true,
      token: token ?? undefined,
    }
  )
}

export interface CampaignSalesFilterOptionsPayload {
  payment_providers: string[]
}

export async function fetchCampaignSalesFilterOptions(
  campaignId: number,
  token?: string | null
): Promise<CampaignSalesFilterOptionsPayload> {
  return apiRequest<CampaignSalesFilterOptionsPayload>(
    `/api/campaigns/${campaignId}/sales/filter-options`,
    {
      method: 'GET',
      requireAuth: true,
      token: token ?? undefined,
    }
  )
}

export async function manualApproveCampaignSale(
  campaignId: number,
  paymentId: number,
  reason: string,
  currentPassword: string,
  token?: string | null
): Promise<CampaignSaleDetailPayload> {
  return updateCampaignSaleStatus(
    campaignId,
    paymentId,
    { status: 'paid', reason, current_password: currentPassword },
    token
  )
}

export async function updateCampaignSaleStatus(
  campaignId: number,
  paymentId: number,
  input: {
    status: CampaignPaymentListStatus
    reason: string
    current_password: string
  },
  token?: string | null
): Promise<CampaignSaleDetailPayload> {
  const detail = await api.patch<CampaignSaleDetailPayload>(
    `/api/campaigns/${campaignId}/sales/${paymentId}/status`,
    input,
    {
      requireAuth: true,
      token: token ?? undefined,
    }
  )

  return {
    ...detail,
    questionnaire: {
      answers: detail.questionnaire?.answers ?? [],
    },
  }
}

export async function fetchCampaignSaleDetail(
  campaignId: number,
  paymentId: number,
  token?: string | null
): Promise<CampaignSaleDetailPayload> {
  const detail = await apiRequest<CampaignSaleDetailPayload>(
    `/api/campaigns/${campaignId}/sales/${paymentId}`,
    {
      method: 'GET',
      requireAuth: true,
      token: token ?? undefined,
    }
  )

  return {
    ...detail,
    questionnaire: {
      answers: detail.questionnaire?.answers ?? [],
    },
  }
}

export type WinnerLookupSearchMode = 'exact' | 'approximation'

export async function fetchCampaignWinnerLookup(
  campaignId: number,
  lotteryNumber: string,
  token?: string | null,
  searchMode: WinnerLookupSearchMode = 'approximation'
): Promise<CampaignWinnerLookupPayload> {
  const query = buildQuery({
    lottery_number: lotteryNumber,
    search_mode: searchMode,
  })

  return apiRequest<CampaignWinnerLookupPayload>(
    `/api/campaigns/${campaignId}/winner-lookup${query}`,
    {
      method: 'GET',
      requireAuth: true,
      token: token ?? undefined,
    }
  )
}

export async function storeCampaignDrawWinner(
  campaignId: number,
  payload: CampaignDrawWinnerSavePayload,
  token?: string | null
): Promise<CampaignDrawWinnerStoredPayload> {
  return api.post<CampaignDrawWinnerStoredPayload>(
    `/api/campaigns/${campaignId}/draw-winner`,
    payload,
    { requireAuth: true, token: token ?? undefined }
  )
}

export interface CampaignNumberRankingFilters {
  mode: CampaignNumberRankingMode
  datetimeFrom: string
  datetimeTo: string
}

export async function fetchCampaignNumberRanking(
  campaignId: number,
  filters: CampaignNumberRankingFilters,
  token?: string | null
): Promise<CampaignNumberRankingPayload> {
  const query = buildQuery({
    mode: filters.mode,
    datetime_from: filters.datetimeFrom,
    datetime_to: filters.datetimeTo,
  })

  return apiRequest<CampaignNumberRankingPayload>(
    `/api/campaigns/${campaignId}/number-ranking${query}`,
    {
      method: 'GET',
      requireAuth: true,
      token: token ?? undefined,
    }
  )
}
